import { reduce } from 'lodash';
import { useCallback, useMemo } from 'react';
import { AnyAction } from 'redux';

import {
	INITIALIZE_DATAKEY,
	RESET_ALL_DIRTY_STATES,
	RESET_DIRTY_STATE,
	SET_ALL_DIRTY_STATES_BLURRED,
	SET_BLURRED,
	SET_FOCUSED,
	SET_MODEL,
	SET_MODEL_PROPERTY,
} from '../actions/types';
import { addValidator } from '../selectors/validations';
import {
	BindInputCommonReturn,
	Declaration,
	FormProps,
	GetErrorProps,
	IncompleteDeclaration,
	InputType,
	Model,
	ModelValue,
	OneFormReduxState,
	ValidationResults,
} from '../types';
import { booleanFromCheckbox } from '../utilities/getters';

const getDefaultDeclarationOptions = (): Declaration<never> => ({
	initialModel: {},
	validation: {},
	resetOnMount: false,
});

export const processDefaultOptions = <FieldNames extends string>(
	declaration: IncompleteDeclaration<FieldNames>,
): Declaration<FieldNames> => {
	const defaultDeclaration = getDefaultDeclarationOptions();
	if (typeof declaration.validation === 'undefined') {
		declaration.validation = defaultDeclaration.validation;
	}
	if (typeof declaration.resetOnMount === 'undefined') {
		declaration.resetOnMount = defaultDeclaration.resetOnMount;
	}
	return declaration as Declaration<FieldNames>;
};

export const useCommonForm = <FieldNames extends string>(
	dataKey: string,
	declaration: Declaration<FieldNames>,
	resource: OneFormReduxState<FieldNames> & {
		validation: ValidationResults<FieldNames>;
	},
	dispatch: React.Dispatch<AnyAction>,
): FormProps<FieldNames> => {
	const onFocus = useCallback(
		(e: {
			target: Pick<
				Pick<
					| React.FocusEvent<HTMLInputElement>
					| React.FocusEvent<HTMLSelectElement>
					| React.FocusEvent<HTMLTextAreaElement>,
					'target'
				>['target'],
				'name'
			>;
		}): void => {
			const { name } = e.target;
			return dispatch({
				type: SET_FOCUSED,
				payload: { dataKey, name },
			});
		},
		[dispatch, dataKey],
	);

	const onBlur = useCallback(
		(e: {
			target: Pick<
				Pick<
					| React.FocusEvent<HTMLInputElement>
					| React.FocusEvent<HTMLSelectElement>
					| React.FocusEvent<HTMLTextAreaElement>,
					'target'
				>['target'],
				'name'
			>;
		}): void => {
			const { name } = e.target;
			return dispatch({
				type: SET_BLURRED,
				payload: { dataKey, name },
			});
		},
		[dispatch, dataKey],
	);

	const setProperty = useCallback(
		(name: FieldNames, value: ModelValue): void => {
			const hasChanged = value !== declaration.initialModel[name];
			return dispatch({
				type: SET_MODEL_PROPERTY,
				payload: { dataKey, name, value, hasChanged },
			});
		},
		[declaration.initialModel, dispatch, dataKey],
	);

	const onChange = useCallback(
		(e: {
			target: Pick<
				Pick<
					| React.ChangeEvent<HTMLInputElement>
					| React.ChangeEvent<HTMLSelectElement>
					| React.ChangeEvent<HTMLTextAreaElement>,
					'target'
				>['target'],
				'name' | 'type' | 'value'
			>;
		}): void => {
			const { name, type, value } = e.target;
			// TODO: how to get better type safety here?
			const fieldName = name as FieldNames;
			if (type === 'checkbox') {
				const event = e as React.ChangeEvent<HTMLInputElement>;
				const model = (
					Object.keys(resource.model).length ? resource.model : declaration.initialModel
				) as Model<FieldNames>;
				const modelValue = model[fieldName];
				const oldCheckboxValue =
					(Array.isArray(modelValue) ? modelValue : modelValue ? [modelValue] : []) || [];
				const arrayValue = event.target.checked
					? oldCheckboxValue.concat(value)
					: oldCheckboxValue.filter((v: string | undefined) => v && v !== value);
				return setProperty(fieldName, arrayValue);
			} else if (type === 'file') {
				const event = e as React.ChangeEvent<HTMLInputElement>;
				const files = event.target.files;
				if (!files) {
					return setProperty(fieldName, []);
				}
				// transform FileList to File[] to play nice with Seamless-Immutable
				const fileArray = [];
				for (let i = 0; i < files?.length; i++) {
					fileArray.push(files[i]);
				}
				return setProperty(fieldName, fileArray);
			}
			return setProperty(fieldName, value);
		},
		[resource.model, declaration.initialModel, setProperty],
	);

	interface BindInputFn {
		(
			name: FieldNames,
			type: 'select',
		): BindInputCommonReturn<HTMLSelectElement, FieldNames> & {
			value: string;
		};
		(
			name: FieldNames,
			type: 'file',
		): BindInputCommonReturn<HTMLInputElement, FieldNames> & {
			value: undefined;
		};
		(
			name: FieldNames,
			type: 'checkbox',
			checkboxValue: string,
		): BindInputCommonReturn<HTMLInputElement, FieldNames> & { checked: boolean; value: string };
		(
			name: FieldNames,
			type?: Exclude<InputType, 'file' | 'checkbox'>,
		): BindInputCommonReturn<HTMLInputElement | HTMLTextAreaElement, FieldNames> & { value: string };
		(
			name: FieldNames,
			type: InputType | 'select',
		): BindInputCommonReturn<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, FieldNames> & {
			value: string | undefined;
			checked?: boolean | undefined;
		};
		(
			name: FieldNames,
			type?: InputType | 'select',
			checkboxValue?: string,
		): BindInputCommonReturn<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, FieldNames> & {
			value: string | undefined;
			checked?: boolean | undefined;
		};
	}

	type BindInputImpl = (
		name: FieldNames,
		type?: InputType | 'select',
		checkboxValue?: string,
	) => ReturnType<BindInputFn>;

	const rawBindInputImpl = useCallback<BindInputImpl>(
		(name: FieldNames, type: InputType | 'select' = 'text', checkboxValue?: string) => {
			const model = (
				Object.keys(resource.model).length ? resource.model : declaration.initialModel
			) as Model<FieldNames>;
			if (typeof model[name] === 'undefined') {
				// eslint-disable-next-line	no-console
				console.error(
					'You are attempting to bindInput for a field that does not exist on your model:',
					name,
					model,
				);
			}
			const commonReturn = {
				name,
				onChange,
				onBlur,
				onFocus,
			};
			if (type === 'checkbox') {
				const checked = booleanFromCheckbox(model[name] as string[], checkboxValue as string);
				return {
					...commonReturn,
					value: checkboxValue,
					checked,
				};
			}
			// React-DOM doesn't allow setting of `value` on `<input type="file">`, so leave it uncontrolled
			const value: Exclude<ModelValue, File[]> | undefined =
				type === 'file' ? undefined : ((model[name] as string) ?? '');
			return {
				...commonReturn,
				value,
			};
		},
		[resource.model, declaration.initialModel, onChange, onBlur, onFocus],
	);

	const bindInputImpl = rawBindInputImpl as BindInputFn;

	const getFormErrorCount = useCallback((): number => {
		return reduce(resource.validation.fields, (count, field) => count + (field ? field.errors.length : 0), 0);
	}, [resource.validation.fields]);

	const getErrorProps = useCallback(
		(
			name: FieldNames,
			showValidationBeforeBlur = false,
		): {
			showValidation: boolean;
			errors: string[];
			isValid: boolean | undefined;
		} => {
			// TODO: warn if fieldName not formProps
			const errors = resource.validation.fields[name]?.errors ?? [];
			const isValid = resource.validation.fields[name]?.isValid;
			const hasBlurred = resource.dirtyState[name]?.hasBlurred ?? false;
			const showValidation = typeof isValid !== 'undefined' && (showValidationBeforeBlur || hasBlurred);
			return {
				showValidation,
				errors,
				isValid,
			};
		},
		[resource.validation.fields, resource.dirtyState],
	);

	interface BindWithErrorPropsFn {
		(
			name: FieldNames,
			type: 'select',
			showValidationBeforeBlur?: boolean,
		): BindInputCommonReturn<HTMLSelectElement, FieldNames> &
			ReturnType<GetErrorProps<FieldNames>> & { value: string };
		(
			name: FieldNames,
			type: 'checkbox',
			checkboxValue: string,
			showValidationBeforeBlur?: boolean,
		): BindInputCommonReturn<HTMLInputElement, FieldNames> &
			ReturnType<GetErrorProps<FieldNames>> & { type: 'checkbox'; value: string; checked: boolean };
		(
			name: FieldNames,
			type: 'file',
			showValidationBeforeBlur?: boolean,
		): BindInputCommonReturn<HTMLInputElement, FieldNames> &
			ReturnType<GetErrorProps<FieldNames>> & { type: 'file'; value: undefined };
		(
			name: FieldNames,
			type?: Exclude<InputType, 'file' | 'checkbox'>,
			showValidationBeforeBlur?: boolean,
		): BindInputCommonReturn<HTMLInputElement | HTMLTextAreaElement, FieldNames> &
			ReturnType<GetErrorProps<FieldNames>> & {
				type: Exclude<InputType, 'file' | 'checkbox'>;
				value: string;
			};
		(
			name: FieldNames,
			type?: InputType | 'select',
			checkboxValueOrShowValidationBeforeBlur?: string | boolean,
			showValidationBeforeBlur?: boolean,
		): BindInputCommonReturn<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, FieldNames> &
			ReturnType<GetErrorProps<FieldNames>> & {
				value: string | undefined;
				type?: InputType;
			};
	}

	type BindWithErrorPropsImpl = (
		name: FieldNames,
		type?: InputType | 'select',
		checkboxValueOrShowValidationBeforeBlur?: string | boolean,
		showValidationBeforeBlur?: boolean,
	) => ReturnType<BindWithErrorPropsFn>;

	const rawBindWithErrorPropsImpl = useCallback<BindWithErrorPropsImpl>(
		(
			name: FieldNames,
			type: InputType | 'select' = 'text',
			checkboxValueOrShowValidationBeforeBlur?: string | boolean,
			showValidationBeforeBlur?: boolean,
		) => {
			if (type === 'checkbox') {
				const checkboxValue = checkboxValueOrShowValidationBeforeBlur as string;

				return {
					type,
					...getErrorProps(name, showValidationBeforeBlur),
					...bindInputImpl(name, type, checkboxValue),
				};
			} else {
				const showValidationBeforeBlur = checkboxValueOrShowValidationBeforeBlur as boolean | undefined;

				return {
					...(type === 'select' ? {} : { type }),
					...getErrorProps(name, showValidationBeforeBlur),
					...bindInputImpl(name, type),
				};
			}
		},
		[getErrorProps, bindInputImpl],
	);

	const bindWithErrorPropsImpl = rawBindWithErrorPropsImpl as BindWithErrorPropsFn;

	const setModel = useCallback(
		(nextModel: Model<FieldNames>): void => {
			const changedFields = reduce(
				nextModel,
				(changed, value, name) => {
					const fieldName = name as FieldNames;
					const hasChanged = nextModel[fieldName] !== declaration.initialModel[fieldName];
					return hasChanged ? [...changed, fieldName] : changed;
				},
				[] as FieldNames[],
			);
			return dispatch({
				type: SET_MODEL,
				payload: { dataKey, model: nextModel, changedFields },
			});
		},
		[declaration.initialModel, dispatch, dataKey],
	);

	const resetDirtyState = useCallback(
		(name: FieldNames): void => {
			return dispatch({
				type: RESET_DIRTY_STATE,
				payload: { dataKey, name },
			});
		},
		[dispatch, dataKey],
	);

	const resetAllDirtyStates = useCallback((): void => {
		return dispatch({
			type: RESET_ALL_DIRTY_STATES,
			payload: { dataKey },
		});
	}, [dispatch, dataKey]);

	const setAllDirtyStatesBlurred = useCallback((): void => {
		return dispatch({
			type: SET_ALL_DIRTY_STATES_BLURRED,
			payload: { dataKey },
		});
	}, [dispatch, dataKey]);

	const result: FormProps<FieldNames> = useMemo(
		() => ({
			...resource,
			onFocus,
			onBlur,
			onChange,
			bindInput: bindInputImpl,
			getFormErrorCount,
			getErrorProps,
			bindWithErrorProps: bindWithErrorPropsImpl,
			setProperty,
			setModel,
			resetDirtyState,
			resetAllDirtyStates,
			setAllDirtyStatesBlurred,
		}),
		[
			resource,
			onFocus,
			onBlur,
			onChange,
			bindInputImpl,
			getFormErrorCount,
			getErrorProps,
			bindWithErrorPropsImpl,
			setProperty,
			setModel,
			resetDirtyState,
			resetAllDirtyStates,
			setAllDirtyStatesBlurred,
		],
	);

	// If this is our first time running, dispatch the initialize event
	const _isInitialized = Object.keys(resource.model).length > 0;
	if (!_isInitialized) {
		addValidator(dataKey, declaration.validation);
		dispatch({
			type: INITIALIZE_DATAKEY,
			payload: { dataKey, initialModel: declaration.initialModel },
		});

		// TODO: refactor this code to follow best practices
		// eslint-disable-next-line react-hooks/immutability
		result.model = declaration.initialModel;
	}

	return result;
};
